// Geo + track math utilities

const EARTH_R = 6371000; // meters

export function haversine(a, b) {
    if (!a || !b) return 0;
    const toRad = (x) => (x * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const la1 = toRad(a.lat);
    const la2 = toRad(b.lat);
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_R * Math.asin(Math.sqrt(h));
}

export function totalDistance(points) {
    let d = 0;
    for (let i = 1; i < points.length; i++) {
        d += haversine(points[i - 1], points[i]);
    }
    return d;
}

export function formatDistance(m) {
    if (!m && m !== 0) return "—";
    if (m < 1000) return `${Math.round(m)} m`;
    return `${(m / 1000).toFixed(2)} km`;
}

export function formatElev(m) {
    if (m === null || m === undefined || isNaN(m)) return "—";
    return `${Math.round(m)} m`;
}

export function computeElevationStats(points) {
    const elevs = points
        .map((p) => p.ele)
        .filter((e) => typeof e === "number" && !isNaN(e));
    if (elevs.length === 0) {
        return { min: null, max: null, climb: 0, descent: 0 };
    }
    let climb = 0;
    let descent = 0;
    for (let i = 1; i < points.length; i++) {
        const a = points[i - 1].ele;
        const b = points[i].ele;
        if (typeof a !== "number" || typeof b !== "number") continue;
        const diff = b - a;
        if (diff > 0) climb += diff;
        else descent += -diff;
    }
    return {
        min: Math.min(...elevs),
        max: Math.max(...elevs),
        climb,
        descent,
    };
}

// Open-Elevation API - free, no key. Used in batches.
export async function fetchElevations(points) {
    if (!points || points.length === 0) return [];
    const url = "https://api.open-elevation.com/api/v1/lookup";
    try {
        const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                locations: points.map((p) => ({
                    latitude: p.lat,
                    longitude: p.lng,
                })),
            }),
        });
        if (!res.ok) throw new Error("Elevation API error");
        const data = await res.json();
        return data.results.map((r) => r.elevation);
    } catch (e) {
        console.warn("Elevation fetch failed", e);
        return points.map(() => null);
    }
}

export function uid() {
    return Math.random().toString(36).slice(2, 10);
}

export function buildElevationProfile(points) {
    // Returns [{d, ele, paved}] cumulative distance + elevation + surface
    const out = [];
    let cum = 0;
    for (let i = 0; i < points.length; i++) {
        if (i > 0) cum += haversine(points[i - 1], points[i]);
        out.push({
            d: cum,
            ele: typeof points[i].ele === "number" ? points[i].ele : null,
            paved: points[i].paved,
            idx: i,
        });
    }
    return out;
}

// Compute paved vs unpaved distance along the track. Segments WITHOUT surface
// data are considered unpaved (pista). Returns null pct only when the track has
// NO surface information at all (e.g. a track never analysed / auto-traced).
export function computeSurfaceStats(points) {
    const known = points.some((p) => p.paved !== undefined);
    if (!known) {
        return { pavedDist: 0, unpavedDist: 0, unpavedPct: null };
    }
    let paved = 0;
    let unpaved = 0;
    for (let i = 1; i < points.length; i++) {
        const d = haversine(points[i - 1], points[i]);
        if (points[i].paved === true) paved += d;
        else unpaved += d; // undefined / false → pista
    }
    const total = paved + unpaved;
    return {
        pavedDist: paved,
        unpavedDist: unpaved,
        unpavedPct: total > 0 ? (unpaved / total) * 100 : null,
    };
}
